import React, {memo, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import * as lscache from "lscache";

import {Button} from "@pg-design/button-module";
import {mb, pb} from "@pg-design/helpers-css";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues} from "@pg-mono/form";
import {isEmpty, pick} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {OTHER_PROPERTIES_ROOMS_FILTER} from "../../../../app/constants/storage_keys";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyRoomFilter} from "../../../../search/utils/update_localstorage_property_room_filter";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {OfferType} from "../../../types/OfferType";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: []
};

export const OfferDetailPropertyList: React.FC<IProps> = memo((props) => {
    const [currentPropertyListType, setCurrentPropertyListType] = useState(props.propertyListType ?? PropertyListType.TABLE);
    const isApartment = props.offer?.is_holiday_location || props.offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(props.offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based rooms filters used on property detail or latest search from local storage
     */
    const localStoragePropertyRoomFilter = getLocalStoragePropertyRoomFilter();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyRoomFilter) ? userPreferencesFilters : localStoragePropertyRoomFilter;

    /*
        Initial clientside data fetch
    */

    useEffect(() => {
        // set property list filters based on user preferences
        const clearedInitialQueryValue = pick(initialQueryValues, [
            "area_0",
            "area_1",
            "rooms_0",
            "rooms_1",
            ...(props.offer.type === OfferType.FLAT ? ["floor_choices"] : [])
        ]);

        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, clearedInitialQueryValue);
        setFilterValues(searchFilters);

        // if browser has stored rooms filter, fetch properties based on it
        if (!isEmpty(localStoragePropertyRoomFilter)) {
            const defaultQueryValues = {page: 1};

            if (props.offerId) {
                // fetch filtered properties only when initial filters has been set
                if (!isEmpty(initialQueryValues)) {
                    fetchOfferDetailProperties(
                        props.offerId,
                        PropertyListTabTypes.FILTER,
                        OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                        defaultQueryValues,
                        initialQueryValues
                    )(dispatch);
                }

                // clear ls stored rooms filter after fetching, we don't want to reuse it
                lscache.set(OTHER_PROPERTIES_ROOMS_FILTER, {});
            }
        }
    }, []);

    useEffect(() => {
        props.onFilterValuesChange(filterValues);
    }, [filterValues]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(props.offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const offerRangePrice =
        (props.offer.region.country === Country.SPAIN && props.offer?.stats?.ranges_price_min) || props.offer.stats?.ranges_price_m2_min || null;

    const priceType = props.offer.region.country === Country.SPAIN && props.offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;

    return (
        <>
            <PropertyListWrapper showBlend={showPropertyListBlend && props.propertyListObj.properties?.length > 0}>
                {props.offer && (
                    <>
                        <Text variant="headline_4" as="h2" css={[pb(1)]}>
                            {propertyListHeadline}
                            {` (${props.offer.stats?.properties_count_for_sale})`}
                        </Text>

                        {offerRangePrice && (
                            <Text variant="headline_5" as="h3" css={[pb(2)]}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">{`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                            currency: props.offer.currency
                                        })}`}</Text>
                                        {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}
                <OfferDetailPropertyListFilters
                    propertyListObj={props.propertyListObj}
                    filterValues={filterValues}
                    offerId={props.offerId}
                    offer={props.offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={setCurrentPropertyListType}
                />

                {currentPropertyListType === PropertyListType.TABLE && (
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offerId={props.offerId}
                        offer={props.offer}
                        gtmData={{
                            listingName: propertyListHeadline
                        }}
                    />
                )}
                {currentPropertyListType === PropertyListType.TILES && (
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={props.propertyListObj}
                        offer={props.offer}
                        gtmData={{
                            listingName: propertyListHeadline
                        }}
                    />
                )}
            </PropertyListWrapper>

            {hasPlanner3D && (
                <div css={buttonsWrapper}>
                    <Planner3dModal offer={props.offer} vendorId={props.offer.vendor.id} css={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Zobacz makietę 3D</Button>
                    </Planner3dModal>
                    <ShowMoreButton variant="filled_secondary" onClick={() => setShowPropertyListBlend(false)} hide={!showPropertyListBlend}>
                        Pokaż więcej rzutów
                    </ShowMoreButton>
                </div>
            )}
        </>
    );
});

const PropertyListWrapper = styled.section<{showBlend: boolean}>`
    position: relative;
    z-index: 1;
    transition: max-height 0.4s ease-in-out;

    ${mb(3)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${mb(3)};
    }

    ${(props) =>
        props.showBlend
            ? css`
                  max-height: 1300px;
                  overflow-y: hidden;

                  &:after {
                      content: "";
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 10rem;
                      background: linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.gray["100"]});
                  }

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 744px;
                  }
              `
            : css`
                  max-height: 8000px;

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 3000px;
                  }
              `};
`;

const buttonsWrapper = css`
    display: flex;
    gap: 2rem;
    justify-content: center;
`;

const planner3dButtonStyle = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const ShowMoreButton = styled(Button)<{hide: boolean}>`
    display: ${(props) => (props.hide ? "none" : "block")};
`;
